import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import Marquee from "react-fast-marquee";
import { motion } from "framer-motion";
import { ShieldCheck, Box, Sparkles } from "lucide-react";
import { Header } from "@/components/Header";
import { ProductCard } from "@/components/ProductCard";
import { CartSheet } from "@/components/CartSheet";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HERO_IMG = "https://images.unsplash.com/photo-1773837383867-38950a442be0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHw0fHxjbGVhciUyMHNob2UlMjBib3glMjBzdG9yYWdlfGVufDB8fHx8MTc4NjM3ODczNHww&ixlib=rb-4.1.0&q=85";

const ALL_CATEGORIES = [
  { key: "storage-boxes", label: "Storage Boxes" },
  { key: "display-cases", label: "Display Cases" },
  { key: "shoe-care", label: "Shoe Care" },
];

export default function Store({ title = "THE COLLECTION", subtitle, categories, showHero = true }) {
  const allowed = categories || ALL_CATEGORIES.map((c) => c.key);
  const filters = [{ key: "all", label: "All" }, ...ALL_CATEGORIES.filter((c) => allowed.includes(c.key))];
  const [products, setProducts] = useState([]);
  const [category, setCategory] = useState("all");
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const q = (new URLSearchParams(location.search).get("q") || "").trim().toLowerCase();

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/products`).then((r) => setProducts(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const visible = products.filter((p) => {
    if (!allowed.includes(p.category)) return false;
    if (category !== "all" && p.category !== category) return false;
    if (q && !`${p.name} ${p.description || ""}`.toLowerCase().includes(q)) return false;
    return true;
  });

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <CartSheet />

      {showHero && (
      <>
      <section className="relative border-b border-border overflow-hidden">
        <div className="noise-overlay" aria-hidden="true" />
        <div className="max-w-7xl mx-auto px-6 py-16 md:py-24 grid md:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="text-primary font-medium text-sm uppercase tracking-[0.3em] mb-6">KicksGuard Supply Company</p>
            <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
              PROTECT YOUR<br />INVESTMENT<span className="text-primary">.</span>
            </h1>
            <p className="mt-6 text-muted-foreground text-base md:text-lg max-w-md">
              Premium storage, display, and care systems built for serious collectors. Your grails deserve better than a closet floor.
            </p>
            <div className="mt-10 flex gap-4">
              <a href="#shop" data-testid="hero-shop-button" className="bg-primary text-white px-8 py-4 font-medium uppercase tracking-wider text-sm hover:bg-foreground transition-colors">
                Shop the Collection
              </a>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7 }} className="border border-border">
            <img src={HERO_IMG} alt="Sneakers over clear storage box" className="w-full h-full max-h-[70vh] object-cover" />
          </motion.div>
        </div>
      </section>

      <div className="bg-foreground text-white py-3 border-b border-border">
        <Marquee speed={40} gradient={false} aria-hidden="true">
          {Array(4).fill(null).map((_, i) => (
            <span key={i} className="font-display text-sm uppercase tracking-[0.25em] mx-8">
              Protect Your Investment <span className="text-primary mx-8">•</span> Premium Sneaker Care <span className="text-primary mx-8">•</span> Slide-Out Storage <span className="text-primary mx-8">•</span> Wall Display Cases <span className="text-primary mx-8">•</span>
            </span>
          ))}
        </Marquee>
      </div>

      <section className="max-w-7xl mx-auto px-6 py-12 grid md:grid-cols-3 gap-px bg-border border border-border">
        {[
          { icon: ShieldCheck, title: "Built to Protect", desc: "UV-resistant, dust-proof, crush-tested storage for every pair." },
          { icon: Box, title: "Storage for Every Setup", desc: "From stackable plastic to LED slide-outs and custom wood cases." },
          { icon: Sparkles, title: "Keep Them Fresh", desc: "Cleaners, pre-treatments, and whiteners trusted by collectors." },
        ].map((f) => (
          <div key={f.title} className="bg-white p-8">
            <f.icon className="text-primary mb-4" size={24} />
            <h3 className="font-display font-semibold text-lg">{f.title}</h3>
            <p className="text-sm text-muted-foreground mt-2">{f.desc}</p>
          </div>
        ))}
      </section>
      </>
      )}

      {!showHero && (
        <section className="border-b border-border" data-testid="category-page-header">
          <div className="max-w-7xl mx-auto px-6 py-14">
            <p className="text-primary text-sm uppercase tracking-[0.3em] mb-3">KicksGuard Supply Company</p>
            <h1 className="font-display font-bold text-4xl sm:text-5xl tracking-tight">{title}<span className="text-primary">.</span></h1>
            {subtitle && <p className="mt-4 text-muted-foreground max-w-xl">{subtitle}</p>}
          </div>
        </section>
      )}

      <section id="shop" className="max-w-7xl mx-auto px-6 py-16">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
          <div>
            <h2 className="font-display font-bold text-3xl tracking-tight">{showHero ? "THE COLLECTION" : "THE LINEUP"}</h2>
            {q && (
              <p data-testid="search-results-banner" className="mt-2 text-sm text-muted-foreground">
                {visible.length} result{visible.length === 1 ? "" : "s"} for “<span className="text-foreground font-medium">{q}</span>”
              </p>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {filters.length > 2 && filters.map((c) => (
              <button
                key={c.key}
                data-testid={`category-filter-${c.key}`}
                onClick={() => setCategory(c.key)}
                className={`px-4 py-2 text-xs uppercase tracking-wider font-medium border transition-colors ${
                  category === c.key ? "bg-foreground text-white border-foreground" : "border-border hover:border-foreground"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="py-24 text-center text-muted-foreground text-sm uppercase tracking-widest">Loading...</div>
        ) : visible.length === 0 ? (
          <div data-testid="empty-inventory-message" className="border border-border py-24 text-center">
            <p className="font-display font-semibold text-xl">{q ? "No matches found." : "Inventory dropping soon."}</p>
            <p className="text-sm text-muted-foreground mt-2">{q ? "Try a different keyword or browse the full lineup." : "Check back shortly — new storage and care gear is on the way."}</p>
          </div>
        ) : (
          <div data-testid="product-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {visible.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        )}
      </section>

      <footer className="border-t border-border mt-12">
        <div className="max-w-7xl mx-auto px-6 py-12 flex flex-col md:flex-row justify-between gap-6">
          <div>
            <p className="font-display font-bold tracking-tight">KICKSGUARD<span className="text-primary">.</span>SUPPLY</p>
            <p className="text-sm text-muted-foreground mt-2">Protect your investment. Every pair, every day.</p>
          </div>
          <p className="text-xs text-muted-foreground self-end">© 2026 KicksGuard Supply Company</p>
        </div>
      </footer>
    </div>
  );
}
